import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import EmailIcon from "@mui/icons-material/Email";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../../config/axiosConfig";

const reasonLabel = (reason) => {
  if (reason === "inquiry") return "General Inquiry";
  if (reason === "complaint") return "Complaint";
  if (reason === "other") return "Other";
  return reason || "—";
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const ContactMessages = () => {
  const user = useSelector((state) => state.auth.user);
  const userDisplayName = user?.name || user?.email || "Admin";

  const [rawMessages, setRawMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending"); // "pending" | "responded" | "all"
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all messages to compute pending and responded counts accurately
      const response = await axiosInstance.get("contact-messages/all");
      const data = response.data?.data || [];
      setRawMessages(data);

      // If currently selected message is updated, refresh its reference
      if (selected?._id) {
        const updatedSelected = data.find((m) => m._id === selected._id);
        if (updatedSelected) {
          setSelected(updatedSelected);
          setAdminNote(updatedSelected.adminNote || "");
        }
      }
    } catch (error) {
      console.error("Error fetching contact messages:", error);
      Swal.fire("Error", "Failed to load contact messages", "error");
    } finally {
      setLoading(false);
    }
  }, [selected?._id]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (selected) {
      setAdminNote(selected.adminNote || "");
    }
  }, [selected]);

  // Derived counts
  const counts = useMemo(() => {
    const total = rawMessages.length;
    const pending = rawMessages.filter((m) => m.status === "pending").length;
    const responded = rawMessages.filter((m) => m.status === "responded").length;
    return { total, pending, responded };
  }, [rawMessages]);

  // Filtered & Searched messages
  const filteredMessages = useMemo(() => {
    return rawMessages.filter((msg) => {
      // Status filter
      if (filter === "pending" && msg.status !== "pending") return false;
      if (filter === "responded" && msg.status !== "responded") return false;

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = (msg.name || "").toLowerCase().includes(q);
        const matchesEmail = (msg.email || "").toLowerCase().includes(q);
        const matchesPhone = (msg.phone || "").toLowerCase().includes(q);
        const matchesReason = (msg.reason || "").toLowerCase().includes(q);
        const matchesMessage = (msg.message || "").toLowerCase().includes(q);
        const matchesNote = (msg.adminNote || "").toLowerCase().includes(q);
        return (
          matchesName ||
          matchesEmail ||
          matchesPhone ||
          matchesReason ||
          matchesMessage ||
          matchesNote
        );
      }

      return true;
    });
  }, [rawMessages, filter, searchQuery]);

  const openMessage = (msg) => {
    setSelected(msg);
    setAdminNote(msg.adminNote || "");
  };

  const closeDetail = () => {
    setSelected(null);
    setAdminNote("");
  };

  const markResponded = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.put(
        `contact-messages/change/${selected._id}`,
        { status: "responded", adminNote }
      );
      Swal.fire({
        icon: "success",
        title: "Marked as Responded",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      });
      setSelected(response.data?.data || null);
      await fetchMessages();
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update message";
      Swal.fire("Error", message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const markPending = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.put(
        `contact-messages/change/${selected._id}`,
        { status: "pending", adminNote }
      );
      Swal.fire({
        icon: "success",
        title: "Moved back to Pending",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      });
      setSelected(response.data?.data || null);
      await fetchMessages();
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update message";
      Swal.fire("Error", message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const saveNoteOnly = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.put(
        `contact-messages/change/${selected._id}`,
        { adminNote }
      );
      Swal.fire({
        icon: "success",
        title: "Admin note saved",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
      setSelected(response.data?.data || null);
      await fetchMessages();
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to save note";
      Swal.fire("Error", message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteMessage = async (id) => {
    const result = await Swal.fire({
      title: "Delete this message?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;

    setActionLoading(true);
    try {
      await axiosInstance.delete(`contact-messages/delete/${id}`);
      Swal.fire({
        icon: "success",
        title: "Message deleted",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      });
      if (selected?._id === id) closeDetail();
      await fetchMessages();
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete message";
      Swal.fire("Error", message, "error");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={{ padding: "12px 0 32px 0", maxWidth: "1400px", margin: "0 auto" }}>
      {/* ============================================================ */}
      {/* 📊 STATS / COUNTING SUMMARY CARDS                            */}
      {/* ============================================================ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        {/* Total Inquiries Card */}
        <div
          onClick={() => {
            setFilter("all");
            closeDetail();
          }}
          style={{
            background: filter === "all" ? "#eff6ff" : "#ffffff",
            border: filter === "all" ? "2px solid #3b82f6" : "1px solid #e2e8f0",
            borderRadius: "16px",
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Total Inquiries
            </span>
            <div
              style={{
                fontSize: "30px",
                fontWeight: 800,
                color: "#1e293b",
                marginTop: "4px",
                lineHeight: 1,
              }}
            >
              {counts.total}
            </div>
            <span style={{ fontSize: "12px", color: "#3b82f6", fontWeight: 500, marginTop: "6px", display: "inline-block" }}>
              All Received Messages
            </span>
          </div>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              backgroundColor: "#dbeafe",
              color: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EmailIcon style={{ fontSize: "26px" }} />
          </div>
        </div>

        {/* Total Pending Messages Card */}
        <div
          onClick={() => {
            setFilter("pending");
            closeDetail();
          }}
          style={{
            background: filter === "pending" ? "#fffbeb" : "#ffffff",
            border: filter === "pending" ? "2px solid #f59e0b" : "1px solid #e2e8f0",
            borderRadius: "16px",
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#b45309",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Total Pending
            </span>
            <div
              style={{
                fontSize: "30px",
                fontWeight: 800,
                color: "#b45309",
                marginTop: "4px",
                lineHeight: 1,
              }}
            >
              {counts.pending}
            </div>
            <span
              style={{
                fontSize: "12px",
                color: counts.pending > 0 ? "#d97706" : "#64748b",
                fontWeight: 600,
                marginTop: "6px",
                display: "inline-block",
              }}
            >
              {counts.pending > 0 ? "⚡ Needs Response" : "✓ All caught up"}
            </span>
          </div>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              backgroundColor: "#fef3c7",
              color: "#d97706",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MarkEmailUnreadIcon style={{ fontSize: "26px" }} />
          </div>
        </div>

        {/* Total Responded Messages Card */}
        <div
          onClick={() => {
            setFilter("responded");
            closeDetail();
          }}
          style={{
            background: filter === "responded" ? "#f0fdf4" : "#ffffff",
            border: filter === "responded" ? "2px solid #22c55e" : "1px solid #e2e8f0",
            borderRadius: "16px",
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            transition: "all 0.2s ease",
          }}
        >
          <div>
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#15803d",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Total Responded
            </span>
            <div
              style={{
                fontSize: "30px",
                fontWeight: 800,
                color: "#15803d",
                marginTop: "4px",
                lineHeight: 1,
              }}
            >
              {counts.responded}
            </div>
            <span
              style={{
                fontSize: "12px",
                color: "#16a34a",
                fontWeight: 500,
                marginTop: "6px",
                display: "inline-block",
              }}
            >
              ✓ Handled & Completed
            </span>
          </div>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              backgroundColor: "#dcfce7",
              color: "#16a34a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MarkEmailReadIcon style={{ fontSize: "26px" }} />
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* 🔍 FILTER TABS & SEARCH BAR                                  */}
      {/* ============================================================ */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          marginBottom: "20px",
          backgroundColor: "#ffffff",
          padding: "12px 16px",
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        {/* Tabs with Counting Badges */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
          {/* Pending Tab */}
          <button
            type="button"
            onClick={() => {
              setFilter("pending");
              closeDetail();
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600,
              backgroundColor: filter === "pending" ? "#f59e0b" : "#f1f5f9",
              color: filter === "pending" ? "#ffffff" : "#475569",
              transition: "all 0.15s ease",
            }}
          >
            <span>Pending</span>
            <span
              style={{
                padding: "2px 8px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: 700,
                backgroundColor: filter === "pending" ? "rgba(0,0,0,0.2)" : "#e2e8f0",
                color: filter === "pending" ? "#ffffff" : "#334155",
              }}
            >
              {counts.pending}
            </span>
          </button>

          {/* Responded Tab */}
          <button
            type="button"
            onClick={() => {
              setFilter("responded");
              closeDetail();
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600,
              backgroundColor: filter === "responded" ? "#16a34a" : "#f1f5f9",
              color: filter === "responded" ? "#ffffff" : "#475569",
              transition: "all 0.15s ease",
            }}
          >
            <span>Responded</span>
            <span
              style={{
                padding: "2px 8px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: 700,
                backgroundColor: filter === "responded" ? "rgba(0,0,0,0.2)" : "#e2e8f0",
                color: filter === "responded" ? "#ffffff" : "#334155",
              }}
            >
              {counts.responded}
            </span>
          </button>

          {/* All Messages Tab */}
          <button
            type="button"
            onClick={() => {
              setFilter("all");
              closeDetail();
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600,
              backgroundColor: filter === "all" ? "#3b82f6" : "#f1f5f9",
              color: filter === "all" ? "#ffffff" : "#475569",
              transition: "all 0.15s ease",
            }}
          >
            <span>All Messages</span>
            <span
              style={{
                padding: "2px 8px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: 700,
                backgroundColor: filter === "all" ? "rgba(0,0,0,0.2)" : "#e2e8f0",
                color: filter === "all" ? "#ffffff" : "#334155",
              }}
            >
              {counts.total}
            </span>
          </button>
        </div>

        {/* Search Input & Refresh Button */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 300px", maxWidth: "420px" }}>
          <div
            style={{
              position: "relative",
              width: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <SearchIcon
              style={{
                position: "absolute",
                left: "10px",
                color: "#94a3b8",
                fontSize: "18px",
              }}
            />
            <input
              type="text"
              placeholder="Search sender, email, phone, text…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px 8px 34px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: "8px",
                  border: "none",
                  background: "transparent",
                  color: "#94a3b8",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                ×
              </button>
            )}
          </div>

          <Tooltip title="Refresh Messages">
            <IconButton
              onClick={fetchMessages}
              disabled={loading}
              size="small"
              style={{
                backgroundColor: "#f1f5f9",
                borderRadius: "8px",
                padding: "8px",
              }}
            >
              <RefreshIcon style={{ fontSize: "18px", color: "#475569" }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* ============================================================ */}
      {/* 📋 MESSAGE LIST & DETAIL SPLIT VIEW                          */}
      {/* ============================================================ */}
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress size={36} />
          <p style={{ marginTop: "12px", color: "#64748b", fontSize: "14px" }}>Loading contact messages…</p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: selected ? "1fr 1fr" : "1fr",
            gap: "20px",
            alignItems: "start",
          }}
        >
          {/* List Column */}
          <div>
            {filteredMessages.length === 0 ? (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px dashed #cbd5e1",
                  borderRadius: "14px",
                  padding: "48px 24px",
                  textAlign: "center",
                  color: "#64748b",
                }}
              >
                <EmailIcon style={{ fontSize: "40px", color: "#cbd5e1", marginBottom: "8px" }} />
                <h4 style={{ margin: "0 0 4px 0", color: "#334155", fontSize: "16px" }}>
                  No messages found
                </h4>
                <p style={{ margin: 0, fontSize: "13px" }}>
                  {searchQuery
                    ? `No messages match "${searchQuery}" in ${filter} view.`
                    : `No ${filter} contact inquiries at this moment.`}
                </p>
              </div>
            ) : (
              filteredMessages.map((msg) => {
                const isSelected = selected?._id === msg._id;
                const isPending = msg.status === "pending";

                return (
                  <div
                    key={msg._id}
                    onClick={() => openMessage(msg)}
                    style={{
                      border: isSelected
                        ? "2px solid #3b82f6"
                        : "1px solid #e2e8f0",
                      borderRadius: "12px",
                      padding: "16px",
                      marginBottom: "12px",
                      cursor: "pointer",
                      backgroundColor: isSelected
                        ? "#f0f7ff"
                        : "#ffffff",
                      boxShadow: isSelected
                        ? "0 4px 12px rgba(59,130,246,0.12)"
                        : "0 1px 2px rgba(0,0,0,0.03)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "12px",
                        marginBottom: "6px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: isPending ? "#f59e0b" : "#16a34a",
                          }}
                        />
                        <strong style={{ fontSize: "15px", color: "#0f172a" }}>
                          {msg.name}
                        </strong>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: "12px",
                            textTransform: "uppercase",
                            backgroundColor: isPending ? "#fef3c7" : "#dcfce7",
                            color: isPending ? "#b45309" : "#15803d",
                          }}
                        >
                          {msg.status || "pending"}
                        </span>
                        <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                          {formatDate(msg.createdAt)}
                        </span>
                      </div>
                    </div>

                    <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "8px" }}>
                      <span>{msg.email}</span>
                      {msg.phone && (
                        <>
                          <span style={{ margin: "0 6px" }}>•</span>
                          <span>{msg.phone}</span>
                        </>
                      )}
                    </div>

                    <div style={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
                      <span
                        style={{
                          backgroundColor: "#f1f5f9",
                          color: "#334155",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {reasonLabel(msg.reason)}
                      </span>
                      <span
                        style={{
                          color: "#475569",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {msg.message || ""}
                      </span>
                    </div>

                    {msg.respondedBy && (
                      <div
                        style={{
                          marginTop: "8px",
                          paddingTop: "6px",
                          borderTop: "1px dashed #e2e8f0",
                          fontSize: "11px",
                          color: "#16a34a",
                          fontWeight: 500,
                        }}
                      >
                        ✓ Responded by {msg.respondedBy}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Detail Column */}
          {selected && (
            <div
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: "14px",
                padding: "20px",
                backgroundColor: "#ffffff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                position: "sticky",
                top: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "12px",
                  marginBottom: "16px",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: "17px", color: "#0f172a" }}>
                    Inquiry Details
                  </h3>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    ID: {selected._id}
                  </span>
                </div>
                <IconButton onClick={closeDetail} size="small">
                  <CloseIcon style={{ fontSize: "18px" }} />
                </IconButton>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                  marginBottom: "16px",
                  backgroundColor: "#f8fafc",
                  padding: "12px",
                  borderRadius: "10px",
                }}
              >
                <div>
                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                    Sender Name
                  </span>
                  <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "14px", marginTop: "2px" }}>
                    {selected.name}
                  </div>
                </div>

                <div>
                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                    Current Status
                  </span>
                  <div style={{ marginTop: "2px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        fontSize: "11px",
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: "12px",
                        textTransform: "uppercase",
                        backgroundColor: selected.status === "pending" ? "#fef3c7" : "#dcfce7",
                        color: selected.status === "pending" ? "#b45309" : "#15803d",
                      }}
                    >
                      {selected.status}
                    </span>
                  </div>
                </div>

                <div>
                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                    Email Address
                  </span>
                  <div style={{ marginTop: "2px" }}>
                    <a
                      href={`mailto:${selected.email}`}
                      style={{ color: "#2563eb", textDecoration: "none", fontSize: "13px", fontWeight: 500 }}
                    >
                      {selected.email}
                    </a>
                  </div>
                </div>

                <div>
                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                    Phone Number
                  </span>
                  <div style={{ marginTop: "2px" }}>
                    <a
                      href={`tel:${selected.phone}`}
                      style={{ color: "#2563eb", textDecoration: "none", fontSize: "13px", fontWeight: 500 }}
                    >
                      {selected.phone || "—"}
                    </a>
                  </div>
                </div>

                <div>
                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                    Category / Reason
                  </span>
                  <div style={{ fontWeight: 600, color: "#334155", fontSize: "13px", marginTop: "2px" }}>
                    {reasonLabel(selected.reason)}
                  </div>
                </div>

                <div>
                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                    Date Received
                  </span>
                  <div style={{ color: "#334155", fontSize: "12px", marginTop: "2px" }}>
                    {formatDate(selected.createdAt)}
                  </div>
                </div>

                {selected.respondedAt && (
                  <div style={{ gridColumn: "span 2" }}>
                    <span style={{ fontSize: "11px", color: "#15803d", fontWeight: 600, textTransform: "uppercase" }}>
                      Responded Info
                    </span>
                    <div style={{ color: "#16a34a", fontSize: "12px", marginTop: "2px" }}>
                      {formatDate(selected.respondedAt)}
                      {selected.respondedBy ? ` by ${selected.respondedBy}` : ""}
                    </div>
                  </div>
                )}
              </div>

              {/* Message Content */}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: "6px", textTransform: "uppercase" }}>
                  Message Content
                </label>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "12px",
                    fontSize: "13px",
                    color: "#1e293b",
                    lineHeight: 1.5,
                    maxHeight: "180px",
                    overflowY: "auto",
                  }}
                >
                  {selected.message}
                </div>
              </div>

              {/* Admin Note Input */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="adminNoteInput"
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#475569",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                  }}
                >
                  Admin Internal Note
                </label>
                <textarea
                  id="adminNoteInput"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={3}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "10px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    fontFamily: "inherit",
                    outline: "none",
                  }}
                  placeholder={`Add an internal follow-up note as ${userDisplayName}…`}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
                {selected.status !== "responded" ? (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={markResponded}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "10px 18px",
                      backgroundColor: "#16a34a",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      boxShadow: "0 2px 4px rgba(22,163,74,0.2)",
                    }}
                  >
                    <CheckCircleOutlineIcon style={{ fontSize: "18px" }} />
                    <span>{actionLoading ? "Saving…" : "Mark as Responded"}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={markPending}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "10px 18px",
                      backgroundColor: "#f59e0b",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      boxShadow: "0 2px 4px rgba(245,158,11,0.2)",
                    }}
                  >
                    <UndoIcon style={{ fontSize: "18px" }} />
                    <span>{actionLoading ? "Saving…" : "Move to Pending"}</span>
                  </button>
                )}

                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={saveNoteOnly}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "10px 16px",
                    backgroundColor: "#f1f5f9",
                    color: "#334155",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  <SaveIcon style={{ fontSize: "18px" }} />
                  <span>Save Note</span>
                </button>

                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => deleteMessage(selected._id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "10px 16px",
                    backgroundColor: "#fef2f2",
                    color: "#dc2626",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginLeft: "auto",
                  }}
                >
                  <DeleteOutlineIcon style={{ fontSize: "18px" }} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactMessages;
